"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Container } from "@/components/layout/container";

const steps = [
  {
    number: "01",
    title: "Imaginer la palette",
    text: "Tout commence par une intention : une chaleur, une tension entre plusieurs teintes, une envie d’accord ou de contraste. Avant le bain, je cherche déjà la respiration de la future palette.",
  },
  {
    number: "02",
    title: "Préparer la laine",
    text: "Je prends le temps d’observer la matière, sa texture, sa souplesse, sa façon d’absorber la couleur. Cette étape est essentielle pour laisser la nuance s’exprimer avec justesse.",
  },
  {
    number: "03",
    title: "Travailler la teinture",
    text: "Le geste, la chaleur, le rythme, la concentration des pigments et la réaction du fil entrent en dialogue. C’est un moment très vivant, où la couleur se construit avec précision mais garde sa part sensible.",
  },
  {
    number: "04",
    title: "Révéler puis composer",
    text: "Une fois la laine teintée, séchée et révélée, je compose les kits et les associations finales. J’accorde beaucoup d’attention à la cohérence d’ensemble, à la vibration des couleurs et à l’émotion qu’elles dégagent.",
  },
];

const pillars = [
  {
    title: "Un geste attentif",
    text: "Chaque teinture est réalisée à la main, avec une attention portée à la matière, à la nuance et à l’équilibre visuel final.",
  },
  {
    title: "Une couleur habitée",
    text: "Je recherche des harmonies chaleureuses, vivantes et originales, avec de la profondeur, du relief et parfois une pointe d’éclat.",
  },
  {
    title: "Une composition sensible",
    text: "Au-delà de la teinture elle-même, j’aime construire de véritables accords entre les laines, pour qu’un kit raconte déjà quelque chose avant même d’être tricoté.",
  },
];

const monoFont = { fontFamily: '"IBM Plex Mono", monospace' };

const pagePalette = {
  background:
    "linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)",
} as React.CSSProperties;

function Reveal({
  children,
  delay = 0,
  className = "",
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.16,
        rootMargin: "0px 0px -8% 0px",
      }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0px)" : "translateY(28px)",
        transition: `opacity 700ms ease, transform 700ms ease ${delay}ms`,
        willChange: "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}

export default function TravailDeTeinturierePage() {
  return (
    <div style={pagePalette}>
      <HeroSection />
      <PillarsSection />
      <ProcessSection />
      <VisualStorySection />
      <ClosingSection />
    </div>
  );
}

function HeroSection() {
  return (
    <section className="section overflow-hidden pb-8 pt-8 sm:pt-10 lg:pt-12">
      <Container>
        <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-end">
          <Reveal className="max-w-xl">
            <p style={monoFont} className="text-[11px] uppercase tracking-[0.22em] text-[var(--accent)]/85">
              Travail de teinturière
            </p>

            <h1 className="mt-4 text-5xl leading-[0.96] sm:text-6xl lg:text-7xl">
              Teindre la laine, pour moi, c’est composer entre geste, intuition et matière.
            </h1>

            <p className="mt-6 text-base leading-7 text-[var(--foreground)]/74 sm:text-lg">
              Derrière chaque kit et chaque pelote, il y a un temps de recherche, de
              préparation et de composition. Mon travail de teinturière consiste à faire
              dialoguer la matière, la couleur et l’intuition pour obtenir des laines
              qui aient à la fois de la présence, de la douceur et une vraie singularité.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                href="/boutique"
                className="rounded-full bg-[var(--primary)] px-6 py-3 text-sm font-medium text-white transition hover:opacity-90"
              >
                Découvrir la boutique
              </Link>

              <Link
                href="/specificites-des-produits"
                className="rounded-full border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] px-6 py-3 text-sm font-medium transition hover:border-[var(--accent)] hover:text-[var(--accent)]"
              >
                Voir les spécificités
              </Link>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="grid gap-5 sm:grid-cols-[0.72fr_1fr]">
              <div className="flex flex-col gap-5 sm:pb-10">
                <div className="rounded-[24px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] p-5 shadow-[var(--shadow)]">
                  <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--primary)]">
                    Geste artisanal
                  </p>
                  <p className="mt-3 text-sm leading-6 text-[var(--foreground)]/74">
                    Une approche sensible, manuelle et vivante, où chaque teinture garde
                    quelque chose d’unique.
                  </p>
                </div>

                <div className="relative overflow-hidden rounded-[28px] shadow-[var(--shadow)]">
                  <div className="relative aspect-[4/5] overflow-hidden rounded-[22px]">
                    <Image
                      src="/images/8.webp"
                      alt="Teinture artisanale Jour J Création"
                      fill
                      sizes="(max-width: 1024px) 100vw, 28vw"
                      className="object-cover"
                    />
                    <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(209,15,115,0.08)_0%,rgba(255,255,255,0)_28%,rgba(217,90,47,0.10)_100%)]" />
                  </div>
                </div>
              </div>

              <div className="relative overflow-hidden rounded-[34px] shadow-[var(--shadow)]">
                <div className="relative aspect-[4/5] overflow-hidden rounded-[28px]">
                  <Image
                    src="/images/9.webp"
                    alt="Palette et matière de laines teintes à la main"
                    fill
                    priority
                    sizes="(max-width: 1024px) 100vw, 34vw"
                    className="object-cover"
                  />
                  <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(209,15,115,0.10)_0%,rgba(255,255,255,0)_26%,rgba(217,90,47,0.12)_100%)]" />
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}

function PillarsSection() {
  return (
    <section className="section pt-0">
      <Container>
        <div className="grid gap-6 md:grid-cols-3">
          {pillars.map((item, index) => (
            <Reveal key={item.title} delay={index * 90}>
              <article className="rounded-[26px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] p-6 shadow-[var(--shadow)]">
                <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--primary)]">
                  Atelier
                </p>
                <h2 className="mt-4 text-2xl leading-tight">{item.title}</h2>
                <p className="mt-4 text-sm leading-6 text-[var(--foreground)]/75">
                  {item.text}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}

function ProcessSection() {
  return (
    <section className="section">
      <Container>
        <Reveal className="mb-10 max-w-2xl">
          <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85">
            Le processus
          </p>
          <h2 className="mt-4 text-4xl leading-tight sm:text-5xl">
            Une suite d’étapes où la couleur se construit, s’ajuste et prend forme.
          </h2>
        </Reveal>

        <div className="grid gap-5">
          {steps.map((step, index) => (
            <Reveal key={step.number} delay={index * 110}>
              <article className="rounded-[28px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] p-6 shadow-[var(--shadow)] sm:p-7">
                <div className="grid gap-5 md:grid-cols-[120px_1fr] md:items-start">
                  <div>
                    <span style={monoFont} className="text-sm uppercase tracking-[0.22em] text-[var(--primary)]">
                      {step.number}
                    </span>
                  </div>

                  <div>
                    <h3 className="text-2xl leading-tight sm:text-3xl">{step.title}</h3>
                    <p className="mt-4 max-w-3xl text-sm leading-7 text-[var(--foreground)]/74 sm:text-base">
                      {step.text}
                    </p>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}

function VisualStorySection() {
  return (
    <section className="section">
      <Container>
        <div className="flex justify-center">
          <Reveal className="w-full max-w-5xl rounded-[32px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] p-5 shadow-[var(--shadow)]">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div>
                <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85">
                  Story visuelle
                </p>
                <h2 className="mt-2 text-3xl leading-tight sm:text-4xl">
                  L’atelier.
                </h2>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-[24px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] p-4">
                <div className="relative aspect-[4/5] overflow-hidden rounded-[20px]">
                  <Image
                    src="/images/7.webp"
                    alt="Nuances et détails de laine teinte à la main"
                    fill
                    sizes="(max-width: 768px) 100vw, 40vw"
                    className="object-cover"
                  />
                </div>
              </div>

              <div className="rounded-[24px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] p-4">
                <div className="relative aspect-[4/5] overflow-hidden rounded-[20px]">
                  <Image
                    src="/images/8.webp"
                    alt="Matière et texture de laines artisanales"
                    fill
                    sizes="(max-width: 768px) 100vw, 40vw"
                    className="object-cover"
                  />
                </div>
              </div>

              <div className="rounded-[24px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] p-4 sm:col-span-2">
                <div className="relative aspect-[16/8] overflow-hidden rounded-[20px]">
                  <Image
                    src="/images/9.webp"
                    alt="Palette et atmosphère de l’atelier de teinture"
                    fill
                    sizes="100vw"
                    className="object-cover"
                  />
                </div>
              </div>
            </div>
          </Reveal>

        </div>
      </Container>
    </section>
  );
}

function ClosingSection() {
  return (
    <section className="section pt-0">
      <Container>
        <div className="rounded-[34px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] p-8 shadow-[var(--shadow)] sm:p-10">
          <Reveal className="max-w-3xl">
            <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85">
              Continuer la découverte
            </p>

            <h2 className="mt-4 text-4xl leading-tight sm:text-5xl">
              Ce travail artisanal devient ensuite une sélection de laines prêtes à rejoindre votre projet.
            </h2>

            <p className="mt-6 max-w-2xl text-base leading-7 text-[var(--foreground)]/74">
              Après le temps de la teinture vient celui du choix, de l’accord et de la
              mise en boutique. C’est là que le travail d’atelier devient une sélection
              de laines prête à rejoindre vos idées, vos envies et vos futurs ouvrages.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                href="/specificites-des-produits"
                className="rounded-full border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] px-6 py-3 text-sm font-medium transition hover:border-[var(--primary)] hover:text-[var(--primary)]"
              >
                Voir les spécificités
              </Link>

              <Link
                href="/boutique"
                className="rounded-full bg-[var(--primary)] px-6 py-3 text-sm font-medium text-white transition hover:opacity-90"
              >
                Explorer la boutique
              </Link>
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}